import template from './ps-action-button.html';

/**
 * Represents an action button that can be displayed in the actions column of a PS Table.
 *
 * A PSActionButtonViewModel defines how an action should be rendered and behaves when clicked.
 * Actions can be displayed as a full button or just an icon depending on the mode configuration.
 *
 * @class PSActionButtonViewModel
 * @example
 * // Create a simple action
 * const action = new PSActionButtonViewModel({
 *   name: 'edit',
 *   label: 'Edit',
 *   icon_class: 'fa fa-edit',
 *   on_click: (row) => console.log('Edit row:', row)
 * });
 *
 * // Create an action with custom styling
 * const action = new PSActionButtonViewModel({
 *   name: 'delete',
 *   label: 'Delete',
 *   mode: 'icon',
 *   class: 'ps-btn-danger',
 *   on_click: async (row) => await deleteRow(row)
 * });
 */
class PSActionButtonViewModel
{
	/**
	 * Creates a new ps-action-button instance.
	 *
	 * @param {Object} params - Action configuration parameters
	 * @param {Object} [params.context] - The context of the action
	 * @param {string} [params.name] - The name/identifier of the action
	 * @param {string} [params.label] - The display text for the action
	 * @param {string} [params.title] - The tooltip text for the action
	 * @param {string} [params.class] - CSS class for styling the action
	 * @param {boolean} [params.visible=true] - Whether the button should be visible. Function, observable or boolean
	 * @param {boolean} [params.enabled=true] - Whether the button should be enabled. Function, observable or boolean
	 * @param {string} [params.icon_class] - CSS class for the icon (e.g., any FontAwesome icon class)
	 * @param {string} [params.mode='icon'] - Display mode: 'button', 'icon'
	 * @param {Function} [params.on_click] - Callback function executed when action is clicked
	 */
	constructor(params, rootElement)
	{
		this.params = params;
		this.rootElement = rootElement;

		this.class = ko_helper.safe_observable(params.class, null);
		this.icon_class = ko_helper.safe_observable(params.icon_class, null);
		this.name = ko_helper.safe_observable(params.name, null);
		this.label = ko_helper.safe_observable(params.label, null);
		this.title = ko_helper.safe_observable(params.title, null);
		this.mode = ko_helper.safe_observable(params.mode, 'icon');
		this.context = ko_helper.safe_observable(params.context, null);

		this.original_visible = this.format_value_declaration(params.visible, true);
		this.original_enabled = this.format_value_declaration(params.enabled, true);

		this.on_click = params.on_click ?? null; // Callback,

		/**
		 * Computed observable that determines if the button should be visible. If the
		 * visible property is a function, it will be called with the context as an argument.
		 */
		this.visible = ko.pureComputed(() =>
		{
			let visible = ko.unwrap(this.original_visible);
			if (visible instanceof Function)
				visible = visible(this.context());
			return visible ?? true;
		});

		/**
		 * Computed observable that determines if the button should be enabled. If the
		 * enabled property is a function, it will be called with the context as an argument.
		 */
		this.enabled = ko.pureComputed(() =>
		{
			let enabled = ko.unwrap(this.original_enabled);
			if (enabled instanceof Function)
				enabled = enabled(this.context());

			return enabled ?? true;
		});

		/**
		 * Computed observable that determines the display mode.
		 * Validates the mode and defaults to 'icon' if invalid.
		 * @type {ko.PureComputed<string>}
		 */
		this.display_mode = ko.pureComputed(() =>
		{
			const allowed_modes = ['button', 'icon'];
			let mode = this.mode()?.toLowerCase();

			if (!allowed_modes.includes(mode))
				mode = 'icon';

			if (mode == 'icon' && !this.icon_class() && this.display_label())
				mode = 'button';

			return mode;
		});

		/**
		 * Computed observable that provides the CSS class for the button.
		 * Defaults to 'ps-btn-default' if no class is specified.
		 * @type {ko.PureComputed<string>}
		 */
		this.button_class = ko.pureComputed(() =>
		{
			let classes = new Set((this.class() ?? 'ps-btn-default').trim().split(' '));
			classes.add('ps-action-button');

			if (this.display_mode() == 'button')
				classes.add('expanded');

			return Array.from(classes).join(' ');
		});

		/**
		 * Computed observable that provides the display label.
		 * Falls back to the name if no label is specified.
		 * @type {ko.PureComputed<string>}
		 */
		this.display_label = ko.pureComputed(() =>
		{
			return this.label() ?? this.name();
		});

		/**
		 * Computed observable that provides the tooltip text.
		 * Falls back to name or label if no title is specified.
		 * @type {ko.PureComputed<string>}
		 */
		this.display_title = ko.pureComputed(() =>
		{
			return this.title() ?? this.name() ?? this.label();
		});
	}

	format_value_declaration(value, default_value)
	{
		if (value instanceof Function && !ko.isObservable(value))
			return value;
		else if (ko.unwrap(value) instanceof Function)
			return ko.unwrap(value);
		else
			return ko_helper.safe_observable(value, default_value);
	}

	/**
	 * Handles the click event for this action.
	 *
	 * @param {Event} event - The DOM event that triggered the click
	 * @returns {Promise<any>} The result of the on_click callback, or undefined if no callback
	 */
	async onClick(event)
	{
		if (this.on_click instanceof Function)
			return await this.on_click(this.context(), event);
		else
			return;
	}
}

export default {
	name: 'ps-action-button',
	viewModel: PSActionButtonViewModel,
	provider: 'ko',
	template: template
};
